import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  Analytics,
  ManageAccounts,
  Map,
  Assessment,
  Home,
  CloudUpload,
  Assignment,
  Category,
  TrendingUp,
  Payment
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCurrentUser, useAdminDashboardCounts } from '../../hooks/useApi';

export const MarketCompassAdmin: React.FC = () => {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const { data: counts } = useAdminDashboardCounts();

  // Show loading spinner while user data is being fetched
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have admin permission (after loading completes)
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  // Section 1: MarketCompass Reports
  const marketCompassReportsPages = [
    {
      title: 'Market Management',
      description: 'View and manage all markets in the system',
      icon: <Map fontSize="large" />,
      action: () => navigate('/admin/manage-markets'),
      color: '#1976d2',
      count: counts?.pendingMarketReports,
      countLabel: 'Pending Reports'
    },
    {
      title: 'Custom Comp Set Reports',
      description: 'Administer Custom Comp Set Reports',
      icon: <Assessment fontSize="large" />,
      action: () => navigate('/admin/hub/marketcompass/reports'),
      color: 'rgb(101, 120, 162)',
      count: counts?.submittedTrendReports,
      countLabel: 'Submitted Reports'
    },
    {
      title: 'Custom Report Instructions',
      description: 'Guidelines for creating and delivering custom reports to clients',
      icon: <Assignment fontSize="large" />,
      action: () => navigate('/admin/market-compass/custom-report-instructions'),
      color: 'rgb(156, 39, 176)'
    }
  ];

  // Section 2: Market Groupings and Rankings
  const groupingsRankingsPages = [
    {
      title: 'MarketCompass Rank Groupings',
      description: 'Manage market groupings for rankings and analytics in market-analytics database',
      icon: <Category fontSize="large" />,
      action: () => navigate('/admin/market-compass/market-rankings/market-groupings'),
      color: '#00695c'
    },
    {
      title: 'Market Rankings',
      description: 'View and manage market performance rankings',
      icon: <TrendingUp fontSize="large" />,
      action: () => navigate('/admin/market-compass/market-rankings'),
      color: '#00897b'
    }
  ];

  // Section 3: Customization Groups
  const customizationGroupsPages = [
    {
      title: 'Market Customization Groupings',
      description: 'Create and manage market grouping packages with custom pricing and Stripe SKUs',
      icon: <Category fontSize="large" />,
      action: () => navigate('/admin/market-groupings'),
      color: '#0288d1'
    }
  ];

  // Section 4: Other
  const otherPages = [
    {
      title: 'Stripe Management',
      description: 'Monitor and manage Stripe subscriptions and invoices',
      icon: <Payment fontSize="large" />,
      action: () => navigate('/admin/market-compass/stripe'),
      color: '#6772e5'
    },
    {
      title: 'Analytics Dashboard',
      description: 'View download statistics and user engagement',
      icon: <Analytics fontSize="large" />,
      action: () => navigate('/admin/analytics'),
      color: '#2e7d32'
    },
    {
      title: 'Data Extract, Upload & Publish',
      description: 'Instructions for data extraction, upload, and publishing processes',
      icon: <CloudUpload fontSize="large" />,
      action: () => navigate('/admin/data-load-instructions'),
      color: '#ed6c02'
    }
  ];

  // Helper function to render a card with optional count badge
  const renderCard = (page: any) => (
    <Grid item xs={12} md={6} lg={4} key={page.title}>
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
        onClick={page.action}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                color: page.color,
                mr: 2,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {page.icon}
            </Box>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {page.title}
            </Typography>
            {page.count !== undefined && page.count > 0 && (
              <Badge
                badgeContent={page.count}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.875rem',
                    height: '24px',
                    minWidth: '24px',
                    borderRadius: '12px'
                  }
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {page.description}
          </Typography>
          {page.count !== undefined && page.count > 0 && page.countLabel && (
            <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
              {page.count} {page.countLabel}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Typography color="text.primary">MarketCompass Admin</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ManageAccounts sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Manage MarketCompass Assets
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Administration tools for market reports and podcasts
          </Typography>
        </Box>

        {/* Section 1: MarketCompass Reports */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            MarketCompass Reports
          </Typography>
          <Grid container spacing={3}>
            {marketCompassReportsPages.map((page) => renderCard(page))}
          </Grid>
        </Box>

        {/* Section 2: Market Groupings and Rankings */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Market Groupings and Rankings
          </Typography>
          <Grid container spacing={3}>
            {groupingsRankingsPages.map((page) => renderCard(page))}
          </Grid>
        </Box>

        {/* Section 3: Customization Groups */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Customization Groups
          </Typography>
          <Grid container spacing={3}>
            {customizationGroupsPages.map((page) => renderCard(page))}
          </Grid>
        </Box>

        {/* Section 4: Other */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Other
          </Typography>
          <Grid container spacing={3}>
            {otherPages.map((page) => renderCard(page))}
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};